"use client";
import React, { useState, useEffect } from 'react';
import { GoalsPayload, LearningPayload, GoalCategory, Timeframe } from '../../../types/onboarding';
import { loadGoals, saveGoals } from '../../../lib/onboarding.store';
import { GoalIcon, PartnerIcon, CommunityIcon, GrowthIcon, EditIcon, AccentIcon } from '../../icons';
import { CreditCard, Mic, BookOpen, Video, Headphones, FileText, MonitorPlay } from 'lucide-react';

export interface GoalsStepRef {
  /** Persist current goals/learning to DB. Call before validating so Next works. */
  saveBeforeNext: () => Promise<void>;
}

interface GoalsStepProps {
  uid: string;
  onNext: () => void;
  onBack: () => void;
  defaultValues?: { goals: GoalsPayload; learning: LearningPayload };
  /** Optional ref so wizard can trigger save before validation (avoids race with auto-save). */
  stepRef?: React.MutableRefObject<GoalsStepRef | null>;
}

const GOAL_CATEGORIES: { id: GoalCategory; label: string; icon: React.ReactNode }[] = [
  { id: 'clients', label: 'Klanten werven', icon: <GoalIcon size={22} weight="regular" className="text-gray-600" /> },
  { id: 'partnerships', label: 'Partnerships', icon: <PartnerIcon size={22} weight="regular" className="text-gray-600" /> },
  { id: 'hiring', label: 'Team uitbreiden', icon: <CommunityIcon size={22} weight="regular" className="text-gray-600" /> },
  { id: 'funding', label: 'Financiering', icon: <CreditCard size={22} className="text-gray-600" /> },
  { id: 'speaking', label: 'Speaking gigs', icon: <Mic size={22} className="text-gray-600" /> },
  { id: 'content', label: 'Content creatie', icon: <EditIcon size={22} weight="regular" className="text-gray-600" /> },
  { id: 'personalBrand', label: 'Personal branding', icon: <AccentIcon size={22} weight="regular" className="text-gray-600" /> },
  { id: 'productLaunch', label: 'Product launch', icon: <GrowthIcon size={22} weight="regular" className="text-gray-600" /> },
  { id: 'community', label: 'Community bouwen', icon: <CommunityIcon size={22} weight="regular" className="text-gray-600" /> }
];

const TIMEFRAMES: { id: Timeframe; label: string }[] = [
  { id: '0-3m', label: '0-3 maanden' },
  { id: '3-6m', label: '3-6 maanden' },
  { id: '6-12m', label: '6-12 maanden' }
];

const LEARNING_SUGGESTIONS = [
  "sales", "marketing", "leadership", "finance", "ai-tools", 
  "operations", "storytelling", "fundraising", "networking", "productivity"
];

const FORMAT_PREFS: { id: string; label: string; icon: React.ReactNode }[] = [
  { id: 'video', label: 'Video', icon: <Video size={20} className="text-gray-600" /> },
  { id: 'audio', label: 'Audio/Podcast', icon: <Headphones size={20} className="text-gray-600" /> },
  { id: 'article', label: 'Artikelen', icon: <FileText size={20} className="text-gray-600" /> },
  { id: 'live', label: 'Live sessies', icon: <MonitorPlay size={20} className="text-gray-600" /> }
];

// Currency conversion rates (approximate, in real app would use API)
const CURRENCY_RATES: Record<string, number> = {
  EUR: 1,
  USD: 0.85,
  GBP: 1.17,
  CHF: 0.93,
  SEK: 0.087,
  NOK: 0.085,
  DKK: 0.134,
  CAD: 0.63,
  AUD: 0.56,
  JPY: 0.0058,
  CNY: 0.12,
  INR: 0.010,
  BRL: 0.16,
  MXN: 0.050,
  ZAR: 0.046,
  AED: 0.23,
  SAR: 0.23
};

const convertToEUR = (amount: number, fromCurrency: string): number => {
  const rate = CURRENCY_RATES[fromCurrency] || 1;
  return amount * rate;
};

export default function GoalsStep({ uid, onNext, onBack, defaultValues, stepRef }: GoalsStepProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Goals state
  const [selectedCategories, setSelectedCategories] = useState<GoalCategory[]>([]);
  const [timeframe, setTimeframe] = useState<Timeframe>('0-3m');
  const [kpis, setKpis] = useState({ leads: '', revenue: '', followers: '', currency: 'EUR' });
  const [goalNotes, setGoalNotes] = useState('');
  
  // Learning state
  const [learningTopics, setLearningTopics] = useState<string[]>([]);
  const [newTopic, setNewTopic] = useState('');
  const [formatPrefs, setFormatPrefs] = useState<string[]>([]);
  const [timeBudget, setTimeBudget] = useState<'1h'|'2h'|'3h+'>('1h');
  const [learningNotes, setLearningNotes] = useState('');

  // Load existing data
  useEffect(() => {
    const loadExistingData = async () => {
      if (defaultValues) {
        setSelectedCategories(defaultValues.goals.categories);
        setTimeframe(defaultValues.goals.timeframe);
        setKpis({
          leads: defaultValues.goals.kpis?.leads?.toString() || '',
          revenue: defaultValues.goals.kpis?.revenue?.toString() || '',
          followers: defaultValues.goals.kpis?.followers?.toString() || '',
          currency: defaultValues.goals.kpis?.currency || 'EUR'
        });
        setGoalNotes(defaultValues.goals.notes || '');
        setLearningTopics(defaultValues.learning.topics);
        setFormatPrefs(defaultValues.learning.formatPrefs || []);
        setTimeBudget(defaultValues.learning.timeBudget || '1h');
        setLearningNotes(defaultValues.learning.notes || '');
      } else {
        setLoading(true);
        try {
          const data = await loadGoals(uid);
          setSelectedCategories(data.goals.categories);
          setTimeframe(data.goals.timeframe);
          setKpis({
            leads: data.goals.kpis?.leads?.toString() || '',
            revenue: data.goals.kpis?.revenue?.toString() || '',
            followers: data.goals.kpis?.followers?.toString() || '',
            currency: data.goals.kpis?.currency || 'EUR'
          });
          setGoalNotes(data.goals.notes || '');
          setLearningTopics(data.learning.topics);
          setFormatPrefs(data.learning.formatPrefs || []);
          setTimeBudget(data.learning.timeBudget || '1h');
          setLearningNotes(data.learning.notes || '');
        } catch (error) {
          console.error('Error loading goals:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadExistingData();
  }, [uid, defaultValues]);

  const toggleCategory = (category: GoalCategory) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const addTopic = () => {
    if (newTopic.trim() && !learningTopics.includes(newTopic.trim())) {
      setLearningTopics(prev => [...prev, newTopic.trim()]);
      setNewTopic('');
    }
  };

  const removeTopic = (topic: string) => {
    setLearningTopics(prev => prev.filter(t => t !== topic));
  };

  const addSuggestedTopic = (topic: string) => {
    if (!learningTopics.includes(topic)) {
      setLearningTopics(prev => [...prev, topic]);
    }
  };

  const toggleFormatPref = (format: string) => {
    setFormatPrefs(prev => 
      prev.includes(format)
        ? prev.filter(f => f !== format)
        : [...prev, format]
    );
  };

  // Auto-save when data changes (topics and/or goal categories)
  // Fix: save when user has learning topics even without selecting goal categories,
  // so validation on Next can pass and the Next button works.
  React.useEffect(() => {
    const hasGoals = selectedCategories.length > 0;
    const hasLearning = learningTopics.length > 0;
    if (uid && !loading && (hasGoals || hasLearning)) {
      const goals: GoalsPayload = {
        categories: selectedCategories.length > 0 ? selectedCategories : ['clients'],
        timeframe,
        kpis: {
          leads: kpis.leads ? parseInt(kpis.leads) : undefined,
          revenue: kpis.revenue ? parseInt(kpis.revenue) : undefined,
          followers: kpis.followers ? parseInt(kpis.followers) : undefined,
          currency: kpis.currency
        },
        notes: goalNotes
      };

      const learning: LearningPayload = {
        topics: learningTopics,
        formatPrefs: formatPrefs as any,
        timeBudget,
        notes: learningNotes
      };

      saveGoals(uid, goals, learning).catch(error => {
        console.error('Error auto-saving goals:', error);
      });
    }
  }, [selectedCategories, timeframe, kpis, goalNotes, learningTopics, formatPrefs, timeBudget, learningNotes, uid, loading]);

  // Expose save so wizard can persist right before validation (no race with auto-save)
  React.useEffect(() => {
    if (!stepRef) return;
    stepRef.current = {
      saveBeforeNext: async () => {
        const goals: GoalsPayload = {
          categories: selectedCategories.length > 0 ? selectedCategories : ['clients'],
          timeframe,
          kpis: {
            leads: kpis.leads ? parseInt(kpis.leads) : undefined,
            revenue: kpis.revenue ? parseInt(kpis.revenue) : undefined,
            followers: kpis.followers ? parseInt(kpis.followers) : undefined,
            currency: kpis.currency
          },
          notes: goalNotes
        };
        const learning: LearningPayload = {
          topics: learningTopics,
          formatPrefs: formatPrefs as any,
          timeBudget,
          notes: learningNotes
        };
        await saveGoals(uid, goals, learning);
      }
    };
    return () => {
      stepRef.current = null;
    };
  }, [stepRef, uid, selectedCategories, timeframe, kpis, goalNotes, learningTopics, formatPrefs, timeBudget, learningNotes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2"><GoalIcon size={28} weight="regular" className="text-gray-700" /> Doelen & Leerdoelen</h2>
        <p className="text-gray-600">Vertel ons waar je naar toe werkt en wat je wilt leren</p>
      </div>

      {/* Goals Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2"><GrowthIcon size={22} weight="regular" className="text-gray-700" /> Waar werk je naar toe?</h3>
        
        {/* Goal Categories */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Selecteer je hoofddoelen (minimaal 1)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {GOAL_CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => toggleCategory(category.id)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  selectedCategories.includes(category.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="mb-1">{category.icon}</div>
                <div className="text-sm font-medium">{category.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Timeframe */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Tijdsframe</label>
          <div className="grid grid-cols-3 gap-3">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf.id}
                onClick={() => setTimeframe(tf.id)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  timeframe === tf.id
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPIs */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            KPI's (optioneel)
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Leads per maand</label>
              <input
                type="number"
                value={kpis.leads}
                onChange={(e) => setKpis(prev => ({ ...prev, leads: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="bijv. 50"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Omzet</label>
              <input
                type="number"
                value={kpis.revenue}
                onChange={(e) => setKpis(prev => ({ ...prev, revenue: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                placeholder="bijv. 100000"
              />
              <select
                value={kpis.currency || 'EUR'}
                onChange={(e) => setKpis(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="EUR">€ EUR</option>
                <option value="USD">$ USD</option>
                <option value="GBP">£ GBP</option>
                <option value="CHF">CHF</option>
                <option value="SEK">SEK</option>
                <option value="NOK">NOK</option>
                <option value="DKK">DKK</option>
                <option value="CAD">$ CAD</option>
                <option value="AUD">$ AUD</option>
                <option value="JPY">¥ JPY</option>
                <option value="CNY">¥ CNY</option>
                <option value="INR">₹ INR</option>
                <option value="BRL">R$ BRL</option>
                <option value="MXN">$ MXN</option>
                <option value="ZAR">R ZAR</option>
                <option value="AED">د.إ AED</option>
                <option value="SAR">ر.س SAR</option>
              </select>
              {kpis.revenue && kpis.currency && kpis.currency !== 'EUR' && (
                <div className="text-xs text-gray-500 mt-1">
                  ≈ €{convertToEUR(parseFloat(kpis.revenue), kpis.currency).toLocaleString('nl-NL')}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Followers</label>
              <input
                type="number"
                value={kpis.followers}
                onChange={(e) => setKpis(prev => ({ ...prev, followers: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="bijv. 10000"
              />
            </div>
          </div>
        </div>

        {/* Goal Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aanvullende notities
            <span className="text-xs text-gray-500 ml-1">({goalNotes.length}/300)</span>
          </label>
          <textarea
            value={goalNotes}
            onChange={(e) => setGoalNotes(e.target.value.slice(0, 300))}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Vertel meer over je doelen..."
          />
        </div>
      </div>

      {/* Learning Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2"><BookOpen size={22} className="text-gray-700" /> Wat wil je leren?</h3>
        
        {/* Learning Topics */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Onderwerpen</label>
          
          {/* Add Topic Input */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTopic()}
              className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Voeg onderwerp toe..."
            />
            <button
              onClick={addTopic}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              +
            </button>
          </div>

          {/* Suggestions */}
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-2">Suggesties:</div>
            <div className="flex flex-wrap gap-2">
              {LEARNING_SUGGESTIONS.filter(s => !learningTopics.includes(s)).map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => addSuggestedTopic(suggestion)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                >
                  + {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Topics */}
          <div className="flex flex-wrap gap-2">
            {learningTopics.map(topic => (
              <span
                key={topic}
                className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {topic}
                <button
                  onClick={() => removeTopic(topic)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Format Preferences */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Voorkeur formaat</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {FORMAT_PREFS.map(format => (
              <button
                key={format.id}
                onClick={() => toggleFormatPref(format.id)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  formatPrefs.includes(format.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="mb-1">{format.icon}</div>
                <div className="text-xs">{format.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Time Budget */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Tijd per week</label>
          <select
            value={timeBudget}
            onChange={(e) => setTimeBudget(e.target.value as any)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="1h">1 uur per week</option>
            <option value="2h">2 uur per week</option>
            <option value="3h+">3+ uur per week</option>
          </select>
        </div>

        {/* Learning Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aanvullende wensen
            <span className="text-xs text-gray-500 ml-1">({learningNotes.length}/300)</span>
          </label>
          <textarea
            value={learningNotes}
            onChange={(e) => setLearningNotes(e.target.value.slice(0, 300))}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Specifieke wensen of vragen..."
          />
        </div>
      </div>

      {/* Note: Navigation handled by parent OnboardingWizard */}
    </div>
  );
}

